
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

interface Article {
  slug: string;
  title: string;
  metaDescription: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
  content: {
    introduction: string;
    sections: {
      title: string;
      content: string;
    }[];
    conclusion: string;
  };
}

const articles: Record<string, Article> = {
  'como-reducir-factura-luz-2025': {
    slug: 'como-reducir-factura-luz-2025',
    title: '7 formas de reducir tu factura de luz en 2025',
    metaDescription: 'Descubre las 7 estrategias más efectivas para reducir tu factura de luz en 2025. Consejos prácticos y reales para ahorrar sin renunciar a tu confort.',
    category: 'luz',
    date: '2025-01-15',
    readTime: '5 min',
    image: 'https://public.readdy.ai/ai/img_res/7d3428808caeef2be9a9ecb551b61971.jpg',
    author: 'Equipo Descuentos en Luz',
    content: {
      introduction: 'La factura de la luz sigue siendo una de las mayores preocupaciones de los hogares españoles. Pero la buena noticia es que existen estrategias reales y efectivas para reducir este gasto sin renunciar a tu confort. En este artículo te mostramos 7 formas comprobadas de pagar menos cada mes.',
      sections: [
        {
          title: '1. Revisa tu potencia contratada',
          content: 'Muchos hogares tienen contratada más potencia de la que realmente necesitan. Esto supone pagar un término fijo más alto cada mes sin motivo. Analiza tu consumo real y ajusta la potencia. En muchos casos, reducir de 4,6 kW a 3,45 kW puede suponer un ahorro de más de 100€ al año.'
        },
        {
          title: '2. Cambia a una tarifa más competitiva',
          content: 'El mercado eléctrico español es muy competitivo. Si llevas años con la misma comercializadora, es muy probable que estés pagando de más. Comparar tarifas y cambiar de compañía puede suponer ahorros de entre 150€ y 300€ anuales. Y lo mejor: el cambio es gratuito y sin cortes de suministro.'
        },
        {
          title: '3. Aprovecha las horas valle',
          content: 'Si tienes tarifa con discriminación horaria, concentra el uso de electrodomésticos de alto consumo (lavadora, lavavajillas, secadora) en las horas valle. El precio del kWh puede ser hasta un 50% más barato en estos tramos horarios.'
        },
        {
          title: '4. Sustituye bombillas por LED',
          content: 'Las bombillas LED consumen hasta un 80% menos que las incandescentes y duran mucho más. La inversión inicial se recupera en pocos meses y el ahorro es permanente. Un hogar medio puede ahorrar más de 100€ al año solo con este cambio.'
        },
        {
          title: '5. Desconecta aparatos en standby',
          content: 'Los aparatos en modo standby (televisores, cargadores, microondas) consumen energía constantemente. Este consumo fantasma puede representar hasta el 10% de tu factura. Usa regletas con interruptor para desconectar completamente los dispositivos cuando no los uses.'
        },
        {
          title: '6. Optimiza el uso de calefacción y aire acondicionado',
          content: 'La climatización representa más del 40% del consumo eléctrico de un hogar. Mantén la temperatura entre 19-21°C en invierno y 24-26°C en verano. Cada grado de diferencia puede suponer un 7% más de consumo. Usa termostatos programables para optimizar el uso.'
        },
        {
          title: '7. Considera instalar placas solares',
          content: 'La energía solar es cada vez más accesible y rentable. Una instalación fotovoltaica puede reducir tu factura hasta un 70%. Con las subvenciones actuales y la compensación de excedentes, el periodo de amortización se ha reducido a 5-7 años en muchos casos.'
        }
      ],
      conclusion: 'Reducir tu factura de luz no requiere grandes sacrificios. Con pequeños cambios de hábitos y decisiones inteligentes sobre tu contrato eléctrico, puedes conseguir ahorros significativos. El primer paso es revisar tu situación actual y detectar dónde están las oportunidades de mejora.'
    }
  },
  'excedentes-solares-como-funcionan': {
    slug: 'excedentes-solares-como-funcionan',
    title: 'Excedentes solares: cómo funcionan y cómo aprovecharlos',
    metaDescription: 'Guía completa sobre excedentes solares: qué son, cómo funcionan, cómo se compensan y cómo maximizar tu ahorro con placas solares.',
    category: 'solar',
    date: '2025-01-12',
    readTime: '6 min',
    image: 'https://readdy.ai/api/search-image?query=residential%20solar%20panels%20on%20modern%20house%20roof%20with%20energy%20meter%20showing%20surplus%20electricity%20production%2C%20bright%20sunny%20day%20with%20clear%20blue%20sky%20and%20green%20landscape&width=1200&height=600&seq=article2&orientation=landscape',
    author: 'Equipo Descuentos en Luz',
    content: {
      introduction: 'Si tienes placas solares o estás pensando en instalarlas, entender cómo funcionan los excedentes solares es fundamental para maximizar tu ahorro. Muchos propietarios de instalaciones fotovoltaicas no aprovechan correctamente sus excedentes y pierden dinero cada mes. En esta guía te explicamos todo lo que necesitas saber.',
      sections: [
        {
          title: '¿Qué son los excedentes solares?',
          content: 'Los excedentes solares son la energía que tus placas solares producen pero que no consumes en ese momento. Por ejemplo, si tus placas generan 3 kWh a mediodía pero solo estás consumiendo 1 kWh, los 2 kWh restantes son excedentes. Esta energía se vierte a la red eléctrica.'
        },
        {
          title: '¿Cómo se compensan los excedentes?',
          content: 'En España existe un mecanismo de compensación simplificada. La energía excedentaria que viertes a la red se valora económicamente y se descuenta de tu factura eléctrica. El precio de compensación suele estar entre 0,05€ y 0,08€ por kWh, dependiendo del precio del mercado y tu comercializadora.'
        },
        {
          title: 'Requisitos para la compensación',
          content: 'Para acogerte a la compensación de excedentes necesitas: 1) Instalación de autoconsumo con potencia máxima de 100 kW, 2) Contrato de compensación con tu comercializadora, 3) Contador bidireccional instalado, 4) No estar dado de alta como productor de energía. La mayoría de instalaciones residenciales cumplen estos requisitos.'
        },
        {
          title: 'Batería virtual: la evolución de los excedentes',
          content: 'La batería virtual es un servicio que ofrecen algunas comercializadoras. Funciona como una hucha energética: el valor económico de tus excedentes se acumula y puedes usarlo en meses con menos producción solar. Es especialmente útil en verano, cuando produces mucha energía que puedes "guardar" para el invierno.'
        },
        {
          title: 'Cómo maximizar el valor de tus excedentes',
          content: 'Para aprovechar al máximo tus excedentes: 1) Elige una comercializadora con buen precio de compensación, 2) Considera contratar batería virtual, 3) Dimensiona correctamente tu instalación para no sobredimensionarla, 4) Revisa mensualmente tu factura para verificar que la compensación se aplica correctamente.'
        },
        {
          title: 'Errores comunes con los excedentes',
          content: 'Muchos propietarios cometen estos errores: no contratar la compensación (pierden el 100% del valor), no revisar las facturas (errores en la compensación), sobredimensionar la instalación (excedentes que no se compensan completamente), o no comparar comercializadoras (diferencias de hasta un 30% en el precio de compensación).'
        }
      ],
      conclusion: 'Los excedentes solares bien gestionados pueden aumentar significativamente la rentabilidad de tu instalación fotovoltaica. No se trata solo de producir energía, sino de asegurarte de que cada kWh generado se traduce en ahorro real en tu factura. Si tienes dudas sobre si estás aprovechando correctamente tus excedentes, solicita una revisión gratuita de tu instalación.'
    }
  },
  'contrato-dual-luz-gas-ventajas': {
    slug: 'contrato-dual-luz-gas-ventajas',
    title: 'Contrato dual de luz y gas: ¿realmente ahorras?',
    metaDescription: 'Análisis completo de los contratos duales de luz y gas: ventajas, desventajas y cuándo realmente merece la pena unificar tus suministros.',
    category: 'dual',
    date: '2025-01-10',
    readTime: '4 min',
    image: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20with%20gas%20stove%20and%20electric%20appliances%20showing%20dual%20energy%20contract%20concept%2C%20warm%20lighting%20and%20contemporary%20design%20with%20energy%20bills%20on%20counter&width=1200&height=600&seq=article3&orientation=landscape',
    author: 'Equipo Descuentos en Luz',
    content: {
      introduction: 'Los contratos duales de luz y gas prometen simplificar tu gestión energética y ahorrar dinero. Pero ¿realmente cumplen esta promesa? En este artículo analizamos cuándo merece la pena unificar tus suministros y cuándo es mejor mantenerlos separados.',
      sections: [
        {
          title: '¿Qué es un contrato dual?',
          content: 'Un contrato dual agrupa el suministro de luz y gas en una única factura con una sola comercializadora. En teoría, esto simplifica la gestión y puede ofrecer descuentos por contratar ambos servicios. Sin embargo, no siempre es la opción más económica.'
        },
        {
          title: 'Ventajas de los contratos duales',
          content: 'Las principales ventajas son: 1) Una sola factura y un único interlocutor, 2) Posibles descuentos por contratar ambos servicios (entre 5% y 15%), 3) Gestión más simple, 4) Ofertas promocionales exclusivas para clientes duales. Para hogares que valoran la simplicidad, puede ser una buena opción.'
        },
        {
          title: 'Desventajas y riesgos',
          content: 'Los inconvenientes: 1) No siempre es la opción más barata (a veces contratar por separado sale mejor), 2) Menor flexibilidad para cambiar solo uno de los suministros, 3) Posibles cláusulas de permanencia, 4) Si la tarifa de uno de los suministros no es competitiva, pierdes dinero. Es fundamental comparar antes de decidir.'
        },
        {
          title: '¿Cuándo merece la pena un contrato dual?',
          content: 'Un contrato dual tiene sentido si: 1) El descuento dual compensa frente a las mejores tarifas individuales, 2) Valoras mucho la simplicidad de gestión, 3) Tu consumo de gas es significativo (calefacción o agua caliente), 4) La comercializadora ofrece buenos precios en ambos suministros. Si solo usas gas para cocinar, probablemente no compense.'
        },
        {
          title: 'Cómo saber si tu contrato dual es bueno',
          content: 'Para evaluar tu contrato dual: 1) Compara el precio del kWh de luz con las mejores tarifas del mercado, 2) Revisa el término fijo de potencia, 3) Compara el precio del gas con otras comercializadoras, 4) Calcula el ahorro real del descuento dual. Si la diferencia es menor del 10%, probablemente puedas encontrar algo mejor.'
        }
      ],
      conclusion: 'Los contratos duales pueden ser una buena opción, pero no son automáticamente la mejor. Lo importante es comparar el coste total (luz + gas) con las mejores alternativas del mercado. En muchos casos, contratar luz y gas por separado con las comercializadoras más competitivas en cada suministro resulta más económico que un contrato dual. Analiza tu caso concreto antes de decidir.'
    }
  }
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? articles[slug] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="text-[#0A5FFF] hover:underline cursor-pointer">
            Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-[#0A5FFF] cursor-pointer">Inicio</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/blog" className="hover:text-[#0A5FFF] cursor-pointer">Blog</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800">{article.title}</span>
        </nav>

        {/* Category Badge */}
        <div className="mb-6">
          <span className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold">
            {article.category.charAt(0).toUpperCase() + article.category.slice(1)}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-[#1F2937] mb-6 leading-tight">
          {article.title}
        </h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <i className="ri-user-line"></i>
            <span>{article.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-calendar-line"></i>
            <span>
              {new Date(article.date).toLocaleDateString('es-ES', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-time-line"></i>
            <span>{article.readTime} de lectura</span>
          </div>
        </div>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden mb-12">
          <img 
            src={article.image} 
            alt={article.title}
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl mb-12">
            <p className="text-lg text-gray-800 leading-relaxed m-0">
              {article.content.introduction}
            </p>
          </div>

          {/* Sections */}
          {article.content.sections.map((section, index) => (
            <div key={index} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1F2937] mb-4 flex items-start gap-3">
                <span className="text-[#FF8F00]">{section.title}</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {section.content}
              </p>
            </div>
          ))}

          {/* Mid-Article CTA */}
          <div className="my-12 bg-gradient-to-br from-[#0A5FFF] to-[#0847B3] rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">
              ¿Quieres saber cuánto puedes ahorrar?
            </h3>
            <p className="text-lg mb-6 text-white/90">
              Sube tu factura y te hacemos un análisis personalizado gratuito
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/subir-factura"
                className="bg-white text-[#0A5FFF] hover:bg-gray-100 px-6 py-3 rounded-full font-bold transition-all inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                📊 Subir mi factura
              </Link>
              <a
                href="https://wa.me/34696237625?text=Hola,%20quiero%20información%20sobre%20ahorro%20energético"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-3 rounded-full font-bold transition-all inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
            </div>
          </div>

          {/* Conclusion */}
          <div className="bg-gray-50 border border-gray-200 p-8 rounded-2xl mb-12">
            <h2 className="text-2xl font-bold text-[#1F2937] mb-4">
              Conclusión
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed m-0">
              {article.content.conclusion}
            </p>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="border-t border-b border-gray-200 py-6 my-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <span className="font-semibold text-gray-700">Compartir artículo:</span>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const url = window.location.href;
                  const text = article.title;
                  window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                }}
                className="w-10 h-10 bg-[#25D366] hover:bg-[#20BA5A] rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
                aria-label="Compartir en WhatsApp"
              >
                <i className="ri-whatsapp-line text-xl"></i>
              </button>
              <button
                onClick={() => {
                  const url = window.location.href;
                  const text = article.title;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }}
                className="w-10 h-10 bg-[#1877F2] hover:bg-[#145DBF] rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
                aria-label="Compartir en Facebook"
              >
                <i className="ri-facebook-fill text-xl"></i>
              </button>
              <button
                onClick={() => {
                  const url = window.location.href;
                  const text = article.title;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                }}
                className="w-10 h-10 bg-[#1DA1F2] hover:bg-[#1A8CD8] rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
                aria-label="Compartir en Twitter"
              >
                <i className="ri-twitter-x-line text-xl"></i>
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Enlace copiado al portapapeles');
                }}
                className="w-10 h-10 bg-gray-600 hover:bg-gray-700 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
                aria-label="Copiar enlace"
              >
                <i className="ri-link text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-br from-[#FF8F00] to-[#FF6F00] rounded-3xl p-12 text-center text-white mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Empieza a ahorrar hoy mismo
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Más de 1.000 hogares ya están pagando menos gracias a nuestro asesoramiento
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/subir-factura"
              className="bg-white text-[#FF8F00] hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-lg transition-all inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              🚀 Revisar mi factura gratis
            </Link>
            <a
              href="tel:+34696237625"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              696 237 625
            </a>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="text-center">
          <button
            onClick={() => navigate('/blog')}
            className="text-[#0A5FFF] hover:text-[#0847B3] font-semibold inline-flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Volver al blog
          </button>
        </div>
      </article>

      <Footer />

      {/* WhatsApp Sticky Button */}
      <a
        href="https://wa.me/34696237625?text=Hola,%20tengo%20una%20consulta%20sobre%20el%20artículo"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#25D366] hover:bg-[#20BA5A] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-50 cursor-pointer"
        aria-label="Contactar por WhatsApp"
      >
        <i className="ri-whatsapp-line text-white text-3xl"></i>
      </a>
    </div>
  );
}
